import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { Logger } from 'src/services/core/logger/logger.service';
import { OrdersService } from 'src/services/data/orders/orders.service';
import { RestaurantService } from 'src/services/data/restaurant/restaurant.service';
import { PaymentInitResponse } from 'src/services/classes/models/payment';
import { NotificationsService } from 'src/services/classes/notifications/notifications.service';
import { CartService } from 'src/services/data/cart/cart.service';
import { PaymentService } from 'src/services/data/payment/payment.service';

const log = new Logger('collect email');

@Component({
  selector: 'app-collect-email',
  templateUrl: './collect-email.component.html',
  styleUrls: ['./collect-email.component.scss'],
})
export class CollectEmailComponent implements OnInit, OnDestroy {
  emailForm!: FormGroup;
  isProcessingPayment = false;
  constructor(
    private location: Location,
    private fb: FormBuilder,
    private ordersService: OrdersService,
    private restaurantService: RestaurantService,
    private notifyService: NotificationsService,
    private cartService: CartService,
    private paymentService: PaymentService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.emailForm = this.fb.group({
      email: ['', Validators.required],
    });
  }

  handleBackNavigation() {
    this.location.back();
  }

  collectEmail() {
    const { email } = this.emailForm.value;
    this.proceedToPayment(email);
  }

  // API request to make payment here
  proceedToPayment(email: string) {
    const restaurantInfo = this.restaurantService.fetchRestaurantInformation();
    const tableNumber = localStorage.getItem('table-number');
    const successRouteBase =
      window && window.location
        ? `${window.location.protocol}//${window.location.host}`
        : 'http://localhost:4040';

    const payload = {
      email,
      restaurantBranchId: restaurantInfo?.branchId,
      paymentMethod: 'online payment',
      tableId: Number(tableNumber),
      callbackUrl: `${successRouteBase}/#/success`,
    };

    log.debug('payload: ', payload);
    this.makePayment(payload);
  }

  // Initialize the payment server-side, then launch the Payaza inline popup.
  async makePayment(payload: any) {
    this.isProcessingPayment = true;
    try {
      const res = (await firstValueFrom(
        this.ordersService.makePayment(payload)
      )) as PaymentInitResponse;

      if (res?.status?.toLowerCase() === 'error' || !res?.data) {
        this.notifyService.publishMessages(
          res?.message || 'Unable to start payment. Please try again.',
          'danger',
          1
        );
        this.isProcessingPayment = false;
        return;
      }

      log.debug('payment-init: ', res.data);

      if (!res.data.transactionReference) {
        log.debug('Init response missing transactionReference', res.data);
        this.notifyService.publishMessages(
          'Payment could not be started (missing reference).',
          'danger',
          1
        );
        this.isProcessingPayment = false;
        return;
      }

      // Keep the order summary for the success page (replaces the old redirect handoff).
      const allOrders = JSON.parse(localStorage.getItem('allOrders') || '[]');
      localStorage.setItem('forSuccessPage', JSON.stringify(allOrders));

      this.launchPayazaCheckout(res.data);
    } catch (error) {
      log.debug('error: ', error);
      this.notifyService.publishMessages(
        'Something went wrong starting your payment.',
        'danger',
        1
      );
      this.isProcessingPayment = false;
    }
  }

  private launchPayazaCheckout(data: PaymentInitResponse['data']) {
    const email = this.emailForm.value?.email || '';
    this.paymentService.launchPayaza(
      data,
      { email },
      {
        onVerified: () => {
          // Fulfillment is confirmed server-side — now it's safe to finish the order.
          localStorage.removeItem('allOrders');
          this.isProcessingPayment = false;
          this.notifyService.publishMessages(
            '🎉 Payment successful! Your order has been placed.',
            'success',
            1
          );
          this.router.navigateByUrl('/success');
        },
        onFailed: () => {
          this.isProcessingPayment = false;
        },
        onClose: () => {
          this.isProcessingPayment = false;
        },
      }
    );
  }

  ngOnDestroy() {}
}
